import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import cedulaIdentidad from "../../assets/img/cedulaImagen.jpg";
import certificadoNac from "../../assets/img/certNacim.jpg";
import tituloBachiller from "../../assets/img/tituloBachiller.jpg";
import selfieIlustracion from "../../assets/img/selfieIlustracion.svg";
import ciAnversoIlustracion from "../../assets/img/ciAnversoIlustracion.svg";
import ciReversoIlustracion from "../../assets/img/ciReversoIlustracion.svg";
import { BiCheckCircle, BiCreditCard, BiSolidCheckCircle } from "react-icons/bi";
import { Link } from "react-router-dom";
import ModalPdfView from "../../seccions/modals/ModalPdfView";
import { httpGet, httpPost, httpPostMultiPart } from "../../http/request";
import { mostrarAlertaDanger, mostrarAlertaSuccess } from "../../functions/alertas";
import { useUsuario } from "../../context/UsuarioContext";
import MisCarrerasEstudiante from "../../componentes/MisCarrerasEstudiante";

const MatriculacionPage = () => {
  const { setSpinnerLoad } = useUsuario();

  const [habilitacion, setHabilitacion] = useState(null);
  const [prepostulante, setPrepostulante] = useState(null);

  const [paso, setPaso] = useState(0);
  const [showTerminos, setShowTerminos] = useState(false);
  const [showSelfie, setShowSelfie] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [selfieListo, setSelfieListo] = useState(false);
  const [costos, setCostos] = useState(null);
  const [transaccion, setTransaccion] = useState(null);

  const [faseIdentidad, setFaseIdentidad] = useState("selfie");
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [ciAnverso, setCiAnverso] = useState(null);
  const [ciReverso, setCiReverso] = useState(null);
  const [ciAnversoPreview, setCiAnversoPreview] = useState(null);
  const [ciReversoPreview, setCiReversoPreview] = useState(null);

  const [modalPdf, setModalPdf] = useState({ show: false });

  const [documentos, setDocumentos] = useState([
    { codigo: "certificadoNacimiento", tipo: "Certificado de Nacimiento", img: certificadoNac, file: null, fileUrl: null },
    { codigo: "cedulaIdentidad", tipo: "Cédula de Identidad", img: cedulaIdentidad, file: null, fileUrl: null },
    { codigo: "tituloBachiller", tipo: "Título de Bachiller", img: tituloBachiller, file: null, fileUrl: null },
  ]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const cargarDatos = useCallback(() => {
    setSpinnerLoad(true);
    Promise.all([
      httpGet("/api/v1/matriculacion/habilitacion"),
      httpGet("/api/v1/matriculacion/prepostulante"),
    ])
      .then(([hab, pre]) => {
        setHabilitacion(hab);
        setPrepostulante(pre);
      })
      .finally(() => setSpinnerLoad(false));
  }, [setSpinnerLoad]);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirProceso = () => {
    if (!habilitacion?.habilitado) {
      mostrarAlertaDanger(habilitacion?.mensaje || "La matriculación no se encuentra habilitada.");
      return;
    }
    if (!prepostulante) {
      mostrarAlertaDanger("No se encontró su pre-registro de postulación.");
      return;
    }
    setShowTerminos(true);
  };

  const reiniciarIdentidad = () => {
    setFaseIdentidad("selfie");
    setSelfiePreview(null);
    setCiAnverso(null);
    setCiReverso(null);
    setCiAnversoPreview(null);
    setCiReversoPreview(null);
  };

  const aceptarTerminos = () => {
    if (!aceptaTerminos) {
      mostrarAlertaDanger("Debe aceptar los términos y condiciones para continuar.");
      return;
    }
    setSpinnerLoad(true);
    httpPost({ aceptado: true }, "/api/v1/matriculacion/terminos")
      .then((d) => {
        if (d) {
          setShowTerminos(false);
          reiniciarIdentidad();
          setShowSelfie(true);
        }
      })
      .finally(() => setSpinnerLoad(false));
  };

  const iniciarCamara = async (facing = "user") => {
    detenerCamara();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (e) {
      mostrarAlertaDanger("No se pudo acceder a la cámara. Verifique los permisos del navegador.");
    }
  };

  const detenerCamara = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (showSelfie) {
      iniciarCamara("user");
    } else {
      detenerCamara();
    }
    return () => detenerCamara();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSelfie]);

  const capturarFrame = (nombreArchivo) =>
    new Promise((resolve) => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || !video.videoWidth) {
        mostrarAlertaDanger("La cámara aún no está lista. Intente nuevamente.");
        resolve(null);
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          resolve(new File([blob], nombreArchivo, { type: "image/jpeg" }));
        },
        "image/jpeg",
        0.9
      );
    });

  const tomarSelfie = () => {
    setSpinnerLoad(true);
    capturarFrame("selfie.jpg")
      .then((file) => {
        if (!file) return null;
        const formData = new FormData();
        formData.append("selfieFile", file);
        return httpPostMultiPart(formData, "/api/v1/matriculacion/selfie").then((d) => (d ? file : null));
      })
      .then((file) => {
        if (file) {
          setSelfiePreview(URL.createObjectURL(file));
          setFaseIdentidad("anverso");
          mostrarAlertaSuccess("Selfie registrada. Ahora sostenga el anverso de su cédula junto a su rostro.");
        }
      })
      .finally(() => setSpinnerLoad(false));
  };

  const tomarAnverso = () => {
    capturarFrame("ci_anverso.jpg").then((file) => {
      if (!file) return;
      setCiAnverso(file);
      setCiAnversoPreview(URL.createObjectURL(file));
      setFaseIdentidad("reverso");
    });
  };

  const tomarReverso = () => {
    capturarFrame("ci_reverso.jpg").then((file) => {
      if (!file) return;
      setCiReverso(file);
      setCiReversoPreview(URL.createObjectURL(file));
      setFaseIdentidad("listo");
    });
  };

  const finalizarIdentidad = () => {
    if (!ciAnverso || !ciReverso) {
      mostrarAlertaDanger("Debe capturar el anverso y el reverso de su cédula de identidad.");
      return;
    }
    const formData = new FormData();
    formData.append("ciAnversoFile", ciAnverso);
    formData.append("ciReversoFile", ciReverso);
    setSpinnerLoad(true);
    httpPostMultiPart(formData, "/api/v1/matriculacion/documento-identidad")
      .then((d) => {
        if (d) {
          setSelfieListo(true);
          setShowSelfie(false);
          setPaso(1);
          mostrarAlertaSuccess("Documento de identidad registrado correctamente");
        }
      })
      .finally(() => setSpinnerLoad(false));
  };

  const openFileDialog = (codigo) => {
    setSelectedDoc(codigo);
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileUrl = URL.createObjectURL(file);
    setDocumentos((prev) => prev.map((d) => (d.codigo === selectedDoc ? { ...d, file, fileUrl } : d)));
    event.target.value = "";
  };

  const subirDocumentos = () => {
    const formData = new FormData();
    let hay = false;
    documentos.forEach((d) => {
      if (d.file) {
        formData.append(d.codigo, d.file);
        hay = true;
      }
    });
    if (!hay) {
      mostrarAlertaDanger("Debe cargar al menos un documento.");
      return;
    }
    setSpinnerLoad(true);
    httpPostMultiPart(formData, "/api/v1/matriculacion/documentos")
      .then((d) => {
        if (d) mostrarAlertaSuccess("Documentos registrados correctamente");
      })
      .finally(() => setSpinnerLoad(false));
  };

  const verCostos = () => {
    setSpinnerLoad(true);
    httpGet("/api/v1/matriculacion/costos")
      .then((d) => {
        if (d) {
          setCostos(d);
          setPaso(2);
        }
      })
      .finally(() => setSpinnerLoad(false));
  };

  const pagar = () => {
    setSpinnerLoad(true);
    httpPost({ idProceso: habilitacion?.idProceso }, "/api/v1/matriculacion/pago/generar")
      .then((d) => {
        if (d) {
          setTransaccion(d);
          if (d.urlRedireccion) {
            window.open(d.urlRedireccion, "_blank");
          }
          mostrarAlertaSuccess("Transacción generada. Complete el pago en la pasarela.");
        }
      })
      .finally(() => setSpinnerLoad(false));
  };

  const conceptos = costos?.conceptos || [];
  const total = costos?.total ?? conceptos.reduce((acc, c) => acc + (Number(c.pagado) || 0), 0);

  return (
    <>
      <div className="content content-fixed bd-b">
        <div className="container pd-x-0 pd-lg-x-10 pd-xl-x-0">
          <div className="d-sm-flex align-items-center justify-content-between">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                  <li className="breadcrumb-item">
                    <Link to={"/"}>Inicio</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link>Trámite</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Matriculación
                  </li>
                </ol>
              </nav>
              <h4 className="mg-b-0">Matriculación en Línea</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="content content-fix">
        <Container className="px-0">
          <Row>
            <Col sm={12}>
              {habilitacion && !habilitacion.habilitado ? (
                <div className="alert alert-danger" role="alert">
                  {habilitacion.mensaje || "La matriculación no se encuentra habilitada."}
                </div>
              ) : (
                <div className="alert alert-warning" role="alert">
                  Es necesario completar la aceptación de términos, la selfie y la carga de documentos.
                </div>
              )}
            </Col>

            <Col sm={12}>
              <div className="card mb-3">
                <div className="card-body">
                  <div className="bd bg-gray-50 pd-y-15 pd-x-15 pd-sm-x-20">
                    <h6 className="tx-15 mg-b-3 fw-bold">Tienes habilitado la matriculación en Línea</h6>
                    <p className="mg-b-0 tx-14">
                      Puedes ahora mismo realizar este proceso para matricularte a la carrera de{" "}
                      <strong>{prepostulante?.programa || habilitacion?.programa || "tu carrera"}</strong>
                    </p>
                    <span className="tx-13 tx-color-03">
                      {habilitacion?.tipoAdmision
                        ? `${habilitacion.tipoAdmision} · Gestión ${habilitacion.gestion}/${habilitacion.periodo}`
                        : ""}
                    </span>

                    <div className="ms-auto text-end">
                      {paso === 0 && (
                        <button className="btn btn-success btn-lg m-1" onClick={abrirProceso}>
                          Procesar Matriculación
                        </button>
                      )}
                      {selfieListo && paso === 1 && (
                        <>
                          <button className="btn btn-secondary btn-lg m-1" onClick={subirDocumentos}>
                            Guardar Documentos
                          </button>
                          <button className="btn btn-primary btn-lg m-1" onClick={verCostos}>
                            Ver Costos
                          </button>
                        </>
                      )}
                      {paso === 2 && (
                        <span className="badge bg-warning tx-13 pd-y-8 pd-x-12">
                          Matrícula pendiente de pago (Fase 2)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col sm={12}>
              <div className="card">
                <div className="card-header pd-y-15 pd-x-20 d-flex align-items-center justify-content-between">
                  <h6 className="tx-uppercase tx-semibold mg-b-0">Documentos Necesarios</h6>
                </div>
                <div className="card-body">
                  <Row className="justify-content-evenly">
                    {documentos.map((d, i) => (
                      <Col key={i} sm={12} md={6} lg={3} className="mg-b-20">
                        <div className="card card-event border">
                          <img
                            src={d.img}
                            className="card-img-top border pointer"
                            alt=""
                            style={{ filter: `blur(0.8px) ${d.fileUrl ? "" : "grayscale(100%) brightness(40%)"}` }}
                          />

                          <div className="card-body tx-13">
                            <h5 className="fw-bold mb-1">
                              <Link
                                onClick={() => {
                                  if (d.fileUrl) setModalPdf({ show: true, pdf: d.fileUrl });
                                }}
                              >
                                {d.fileUrl ? (
                                  <span className="me-1">
                                    <BiSolidCheckCircle className="text-success fs-4" />
                                  </span>
                                ) : (
                                  <></>
                                )}
                                {d.tipo}{" "}
                              </Link>
                            </h5>
                            <span className="tx-12 tx-color-03">
                              {d.fileUrl
                                ? "Documento Cargado"
                                : "Debe subir el documento escaneado para poder continuar con la matriculación en línea"}
                            </span>
                          </div>
                          <div className="card-footer tx-13">
                            {d.fileUrl && (
                              <button className="btn btn-link" onClick={() => setModalPdf({ show: true, pdf: d.fileUrl })}>
                                Ver
                              </button>
                            )}
                            <button className="btn btn-xs btn-secondary ms-auto" onClick={() => openFileDialog(d.codigo)}>
                              Subir Documento
                            </button>
                          </div>
                        </div>
                      </Col>
                    ))}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      accept="application/pdf"
                      onChange={handleFileChange}
                    />
                  </Row>
                </div>
              </div>
            </Col>

            {paso === 2 && (
              <Col sm={12} className="mg-t-20">
                <div className="card">
                  <div className="card-header pd-y-15 pd-x-20 d-flex align-items-center justify-content-between">
                    <h6 className="tx-uppercase tx-semibold mg-b-0">Costos de Matriculación</h6>
                    <span className="tx-13 tx-color-03">
                      {costos?.clasificacion ? `Clasificación: ${costos.clasificacion}` : ""}
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-dashboard mg-b-0">
                      <thead>
                        <tr>
                          <th className="fw-bold">Concepto</th>
                          <th className="fw-bold text-end">Monto (Bs.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conceptos.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="text-center tx-color-03">
                              No se encontraron conceptos para esta clasificación.
                            </td>
                          </tr>
                        ) : (
                          conceptos.map((c, i) => (
                            <tr key={i}>
                              <td>{c.concepto}</td>
                              <td className="text-end">{Number(c.pagado || 0).toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th className="fw-bold">Total</th>
                          <th className="fw-bold text-end">{Number(total).toFixed(2)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="card-footer pd-y-15 pd-x-20 d-flex align-items-center justify-content-between">
                    <span className="tx-13 tx-color-03">
                      {transaccion?.idTransaccion ? `Transacción: ${transaccion.idTransaccion}` : ""}
                    </span>
                    <button className="btn btn-success" onClick={pagar} disabled={conceptos.length === 0}>
                      <BiCreditCard className="me-1" /> Pagar en línea
                    </button>
                  </div>
                </div>
              </Col>
            )}
            <MisCarrerasEstudiante />
          </Row>
        </Container>
      </div>

      <Modal
        show={showTerminos}
        size="lg"
        onHide={() => setShowTerminos(false)}
        centered
        backdrop="static"
        backdropClassName="z-infini"
        className="z-infini"
      >
        <Modal.Body>
          <h5 className="tx-18 tx-sm-20 mg-b-15">Términos y Condiciones</h5>
          <div className="tx-14" style={{ maxHeight: "45vh", overflowY: "auto" }}>
            <p>
              Declaro que la información registrada es verídica y me comprometo a entregar la documentación física
              (certificado de nacimiento, cédula de identidad y título de bachiller) en un plazo máximo de{" "}
              <strong>tres (3) meses</strong> a partir de la presente matrícula.
            </p>
            <p>
              Entiendo que el incumplimiento de este compromiso puede derivar en la anulación de mi matrícula y en las
              sanciones que la Universidad Amazónica de Pando determine.
            </p>
            <p className="mg-b-0">
              Asimismo, autorizo el tratamiento de mis datos personales y la captura de una selfie como mecanismo de
              verificación de identidad para completar el proceso de matriculación en línea.
            </p>
          </div>
          <div className="form-check mg-t-20">
            <input
              className="form-check-input"
              type="checkbox"
              id="aceptaTerminos"
              checked={aceptaTerminos}
              onChange={(e) => setAceptaTerminos(e.target.checked)}
            />
            <label className="form-check-label tx-14" htmlFor="aceptaTerminos">
              Acepto los términos y condiciones
            </label>
          </div>
          <div className="mt-3 text-end">
            <Button variant="secondary" className="me-2" onClick={() => setShowTerminos(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={aceptarTerminos}>
              Aceptar y continuar
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showSelfie}
        size="md"
        onHide={() => setShowSelfie(false)}
        centered
        backdrop="static"
        backdropClassName="z-infini"
        className="z-infini"
      >
        <Modal.Body>
          <h5 className="tx-18 tx-sm-20 mg-b-15">
            Verificación de identidad {faseIdentidad === "selfie" ? "(selfie)" : "(documento de identidad)"}
          </h5>
          <div className="tx-13 tx-color-03">
            {faseIdentidad === "selfie" && (
              <>
                <p className="mg-b-5">
                  Ubique su rostro en el centro del encuadre, con buena iluminación y sin gorra ni lentes.
                </p>
                <p className="mg-b-0">
                  Mantenga el rostro descubierto y presione «Tomar selfie y continuar».
                </p>
              </>
            )}
            {faseIdentidad === "anverso" && (
              <>
                <p className="mg-b-5">
                  Sostenga el anverso de su cédula junto a su rostro, a la altura del mentón.
                </p>
                <p className="mg-b-0">
                  Verifique que se vean su rostro, la fotografía y los datos de la cédula, sin reflejos, y presione
                  «Capturar anverso».
                </p>
              </>
            )}
            {faseIdentidad === "reverso" && (
              <>
                <p className="mg-b-5">
                  Sin mover el rostro del encuadre, gire la cédula y muestre el reverso junto a su rostro.
                </p>
                <p className="mg-b-0">
                  Confirme que el código de barras se vea completo y presione «Capturar reverso».
                </p>
              </>
            )}
            {faseIdentidad === "listo" && (
              <>
                <p className="mg-b-5">
                  Revise las tres capturas: su rostro debe verse con claridad y la cédula completa en ambas caras.
                </p>
                <p className="mg-b-0">Si todo está correcto, presione «Guardar y continuar».</p>
              </>
            )}
          </div>
          <div className="d-flex justify-content-center align-items-start mg-b-15">
            <div
              className={`text-center border rounded pd-x-10 pd-y-8 me-2 ${
                faseIdentidad === "selfie" ? "border-primary bg-light" : ""
              }`}
            >
              <img
                src={selfiePreview || selfieIlustracion}
                alt="Selfie"
                width={48}
                height={48}
                className="rounded border"
                style={{ objectFit: "cover" }}
              />
              <div className="tx-11 tx-color-03 mg-t-5">Selfie</div>
            </div>
            <div
              className={`text-center border rounded pd-x-10 pd-y-8 me-2 ${
                faseIdentidad === "anverso" ? "border-primary bg-light" : ""
              }`}
            >
              <img
                src={ciAnversoPreview || ciAnversoIlustracion}
                alt="Anverso"
                width={48}
                height={48}
                className="rounded border"
                style={{ objectFit: "cover" }}
              />
              <div className="tx-11 tx-color-03 mg-t-5">Anverso</div>
            </div>
            <div
              className={`text-center border rounded pd-x-10 pd-y-8 ${
                faseIdentidad === "reverso" || faseIdentidad === "listo" ? "border-primary bg-light" : ""
              }`}
            >
              <img
                src={ciReversoPreview || ciReversoIlustracion}
                alt="Reverso"
                width={48}
                height={48}
                className="rounded border"
                style={{ objectFit: "cover" }}
              />
              <div className="tx-11 tx-color-03 mg-t-5">Reverso</div>
            </div>
          </div>
          <div className="text-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-100 rounded border"
              style={{ maxHeight: "320px", background: "#000" }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
          <div className="mt-3 text-center">
            <Button variant="secondary" className="me-2" onClick={() => setShowSelfie(false)}>
              Cancelar
            </Button>
            {faseIdentidad === "selfie" && (
              <Button variant="primary" onClick={tomarSelfie}>
                <BiCheckCircle className="me-1" /> Tomar selfie y continuar
              </Button>
            )}
            {faseIdentidad === "anverso" && (
              <Button variant="primary" onClick={tomarAnverso}>
                <BiCheckCircle className="me-1" /> Capturar anverso
              </Button>
            )}
            {faseIdentidad === "reverso" && (
              <Button variant="primary" onClick={tomarReverso}>
                <BiCheckCircle className="me-1" /> Capturar reverso
              </Button>
            )}
            {faseIdentidad === "listo" && (
              <>
                <Button variant="secondary" className="me-2" onClick={reiniciarIdentidad}>
                  Repetir
                </Button>
                <Button variant="primary" onClick={finalizarIdentidad}>
                  <BiCheckCircle className="me-1" /> Guardar y continuar
                </Button>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <ModalPdfView show={modalPdf.show} onHide={() => setModalPdf({ show: false })} pdf={modalPdf.pdf} />
    </>
  );
};

export default MatriculacionPage;
